<script>
import Auth from "@/services/auth.js";
import Utils from "@/services/utils";
import {Icon} from "@iconify/vue2";

export default {
  name: "UiNavbarItem",
  components: {Icon},
  props: {
    item: {
      type: Object,
      default: () => {
      },
    },
  },
  data() {
    return {
      isOpen: false,
      hasActiveChild: false,
      oldClientUrls: {
        alert_history_denied: "/access-denied/alert-history",
        realtime: "/home",
        realtime_denied: "/access-denied/dashboard",
        kpi_denied: "/access-denied/kpi",
        reports: "/reports",
        reports_denied: "/access-denied/reports",
        devices: "/devices/list",
        devices_denied: "/access-denied/devices",
        groups: "/devices/groups",
        groups_denied: "/access-denied/groups",
        licenses: "/devices/licences",
        licenses_denied: "/access-denied/licences",
        profiles: "/configurations/profiles",
        profiles_denied: "/access-denied/profiles",
        watchmen: "/configurations/watchmen",
        watchmen_denied: "/access-denied/watchmen",
        remotemonitoring: "/remotemonitoring",
        remotemonitoring_denied: "/access-denied/remotemonitoring",
        phonebook: "/parameters/phonebooks",
        phonebook_denied: "/access-denied/phonebooks",
        notification: "/parameters/notifications/new",
        notification_denied: "/access-denied/sendNotifications",
        planning: "/parameters/planning",
        planning_denied: "/access-denied/planning",
        ppes: "/parameters/ppes",
        ppes_denied: "/access-denied/ppes",
        tos: "/parameters/tos",
        tos_denied: "/access-denied/tos",
        identifications: "/parameters/identifications",
        identifications_denied: "/access-denied/identifications",
        patrols_recent: "/patrols/recent",
        patrols_recent_denied: "/access-denied/patrols",
        patrols_list: "/patrols/list",
        patrols_list_denied: "/access-denied/patrols",
        patrols_compare: "/patrols/compare",
        patrols_compare_denied: "/access-denied/patrols",
        actionpoints: "/plans/actionpoints/library",
        actionpoints_denied: "/access-denied/actionpoints-library",
        plans_manager: "/plans/manager",
        plans_manager_denied: "/access-denied/plans",
        certificate: "/certificate",
        certificate_denied: "/access-denied/certificate",
        offers: "/settings/offers",
        offers_denied: "/access-denied/offers",
        formation_denied: "/access-denied/formation",
        me: "/settings/me",
        news: "/news",
      },
      itemClassNames:
          "d-flex items-center tw-px-3 tw-py-2 text-md tw-gap-2 leading-tight rounded-md w-full text-gray-500 hover:text-gray-900 hover:bg-gray-50",
      itemClassNamesActive:
          "font-medium text-gray-900 bg-gray-100 hover:bg-gray-100 hover:text-gray-900",
    };
  },
  computed: {
    childrenList: function () {
      if (!this.item?.children) {
        return [];
      }
      // if a child have onlyForTheme && onlyForTheme is equal to utils.getThemeName() then we filter it
      return this.item.children.filter(
          (child) =>
              !child.onlyForTheme || child.onlyForTheme == Utils.getThemeName()
      );
    },
    hasChildren: function () {
      return !!this.childrenList.length;
    },
    iconName: function () {
      if (!this.item?.icon) {
        return false;
      }
      if (this.item.icon.startsWith("i-material-symbols")) {
        return this.item.icon.replace(
            "i-material-symbols-",
            "material-symbols:"
        );
      }
      return this.item.icon;
    },
  },
  methods: {
    displayItem: function (item) {
      return !item.onlyForTheme || item.onlyForTheme == Utils.getThemeName();
    },
    getInternalUrl: function (url) {
      return url.startsWith("/redirect/") ? url : this.oldClientUrls[url];
    },
    getUrlAttr: function ({ to, target }) {
      return !to.startsWith("http")
          ? {
            is: "router-link",
            to: this.getInternalUrl(to),
            activeClass: [
              "router-link-active quickfix-primary-color-when-router-link-active",
              this.itemClassNamesActive,
            ].join(" "),
          }
          : {
            is: "a",
            href: to.replace("{key}", Auth.key),
            target
          };
    },
    getHasActiveChild: function () {
      const path = window.location.hash.split("#")[1];
      if (!this.hasChildren || path.length <= 1) {
        return false;
      }
      return this.childrenList.some((child) => {
        if (path.startsWith(this.getInternalUrl(child.to))) {
          return true;
        }
      });
    },
    onRouteChange() {
      if (this.getHasActiveChild()) {
        this.isOpen = true;
        this.hasActiveChild = true;
      } else {
        this.isOpen = false;
        this.hasActiveChild = false;
      }
    },
  },
  mounted() {
    if (this.getHasActiveChild()) {
      this.isOpen = true;
      this.hasActiveChild = true;
    }
  },
  watch: {
    $route: {
      immediate: true,
      handler: "onRouteChange",
    },
  },
};
</script>
<template>
  <li v-if="displayItem(item)">
    <component
        v-if="!hasChildren"
        v-bind="getUrlAttr(item)"
        :class="itemClassNames"
        style="box-shadow: inset 0 0 0 1px white"
    >
      <span v-if="item.icon" class="text-gray-400">
        <Icon
            :icon="iconName"
            class="size-5 quickfix-primary-color-when-router-link-active"
        />
      </span>
      <span v-else-if="item.avatar" class="text-gray-400">
        <img
            :src="item.avatar.src"
            :alt="item.avatar.alt"
            height="24"
            width="24"
        />
      </span>
      <span :class="['flex-1', item.labelClass && 'text-gradient-orange']">{{
          $t(item.label)
        }}</span>
      <span
          v-if="item.badge"
          class="d-inline-flex items-center font-medium rounded-md text-xs tw-px-1.5 tw-py-0.5 tw-gap-0.5 bg-gray-50 dark:bg-gray-400 dark:bg-opacity-10 dark:text-gray-400 ring-1 ring-inset ring-gray-500 dark:ring-gray-400 ring-opacity-25 dark:ring-opacity-25 text-gray-700"
      >
        {{ item.badge }}
      </span>
    </component>
    <div v-else>
      <button
          @click="isOpen = !isOpen"
          :class="[
          'border-0 text-left bg-white',
          itemClassNames,
          hasActiveChild && itemClassNamesActive,
        ]"
      >
        <span v-if="item.icon" class="text-gray-400">
          <Icon
              :icon="iconName"
              class="size-5"
              :class="hasActiveChild && 'text-primary-500'"
          />
        </span>
        <span class="flex-1">{{ $t(item.label) }}</span>
        <span
            v-if="item.badge"
            class="d-inline-flex items-center font-medium rounded-md text-xs tw-px-1.5 tw-py-0.5 tw-gap-0.5 bg-gray-50 dark:bg-gray-400 dark:bg-opacity-10 dark:text-gray-400 ring-1 ring-inset ring-gray-500 dark:ring-gray-400 ring-opacity-25 dark:ring-opacity-25 text-gray-700"
        >
          {{ item.badge }}
        </span>
        <span
            class="transform transition-transform duration-200"
            :class="isOpen && 'rotate-90'"
            style="margin-inline-end: -0.25rem"
        >
          <Icon icon="material-symbols:chevron-right" class="size-5"/>
        </span>
      </button>
      <nav class="transition-wrapper" :class="isOpen && 'transition-is-open'">
        <div class="transition-inner">
          <ul class="list-unstyled pb-3">
            <li
                v-for="child in childrenList"
                :key="child.label"
                class="text-sm leading-4"
            >
              <component
                  v-bind="getUrlAttr(child)"
                  class="d-flex flex-1 tw-gap-2 tw-ps-10 tw-pe-2.5 tw-py-1.5 w-full text-sm leading-4 rounded-md hover:text-gray-900 hover:bg-gray-50"
                  style="min-height: 29.5px"
              >
                {{ $t(child.label) }}
                <span
                    v-if="child.badge"
                    class="d-inline-flex items-center font-medium rounded-md text-xs tw-px-1.5 tw-py-0.5 tw-gap-0.5 bg-gray-50 dark:bg-gray-400 dark:bg-opacity-10 dark:text-gray-400 ring-1 ring-inset ring-gray-500 dark:ring-gray-400 ring-opacity-25 dark:ring-opacity-25 text-gray-700"
                >
                  {{ child.badge }}
                </span>
              </component>
            </li>
          </ul>
        </div>
      </nav>
    </div>
  </li>
</template>
<style lang="scss" scoped>
.text-gradient-orange {
  background: linear-gradient(to right, #f56a00 0%, #f8aa00 65%);
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
}

.transition-wrapper {
  display: grid;
  grid-template-rows: 0fr;
  transition: grid-template-rows 0.2s ease-out;
}

.transition-wrapper.transition-is-open {
  grid-template-rows: 1fr;
}

.transition-inner {
  overflow: hidden;
}

// quickfix:
.router-link-active .quickfix-primary-color-when-router-link-active {
  color: var(--colors-primary-500);
}
</style>
